import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  FaExclamationCircle,
  FaHeart,
  FaRegHeart,
  FaShareAlt,
  FaStar,
  FaTimes,
} from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { API_BASE } from "../config/api";
import useCart from "../hooks/useCart";
import useWishlist from "../hooks/useWishlist";
import { normalizeMaterialLabel } from "../utils/materialLabels";
import { bindStorefrontRefresh } from "../utils/storefrontRefresh";
import "./ProductDetail.css";

const LOW_STOCK_THRESHOLD = 5;
const SHIPPING_CARD_ITEMS = [
  "Free delivery above \u20B91000",
  "Delivery within 5\u20138 business days",
];

const formatCurrency = (value) => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "₹0";
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatReviewDate = (value) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const normalizeBulletLine = (value = "") =>
  String(value || "")
    .replace(/^[\s\-*•]+/, "")
    .trim();

const getShippingCardItems = () => SHIPPING_CARD_ITEMS;

const getDescriptionPoints = (product = {}) => {
  const description = String(product?.description || "").trim();
  const multiLinePoints = description
    .split(/\r?\n+/)
    .map(normalizeBulletLine)
    .filter(Boolean);

  if (multiLinePoints.length > 1) {
    return multiLinePoints;
  }

  if (multiLinePoints.length === 1) {
    const sentencePoints = multiLinePoints[0]
      .split(/\.\s+/)
      .map(normalizeBulletLine)
      .filter(Boolean)
      .map((item) => (/[.!?]$/.test(item) ? item : `${item}.`));

    if (sentencePoints.length > 1) {
      return sentencePoints;
    }
  }

  const factualFallback = [];

  if (description) {
    factualFallback.push(description);
  }

  if (product?.material) {
    factualFallback.push(`Material: ${normalizeMaterialLabel(product.material)}`);
  }

  if (product?.type) {
    factualFallback.push(`Style: ${product.type}`);
  }

  if (Array.isArray(product?.availableSizes) && product.availableSizes.length > 0) {
    factualFallback.push(`Available sizes: ${product.availableSizes.join(", ")}`);
  }

  return factualFallback.length > 0
    ? factualFallback
    : ["A refined jewellery piece curated for elevated everyday and occasion styling."];
};

const getPrecautionPoints = (product = {}) => {
  const rawValues = Array.isArray(product?.precautions)
    ? product.precautions
    : typeof product?.precautions === "string"
    ? product.precautions.split(/[,\n]+/)
    : [];

  const normalizedValues = rawValues
    .map(normalizeBulletLine)
    .filter(Boolean);

  return normalizedValues.length > 0
    ? normalizedValues
    : [
        "Keep away from perfumes and harsh chemicals.",
        "Store in a dry place away from moisture and dust.",
      ];
};

const getStockQuantity = (value) => {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) ? Math.max(0, Math.floor(parsedValue)) : 0;
};

const getDisplaySku = (product = {}) => {
  const explicitSku = String(product?.sku || "").trim().toUpperCase();

  if (explicitSku) {
    return explicitSku;
  }

  const productId = String(product?._id || "").trim();
  return productId ? `PJ-${productId.slice(-8).toUpperCase()}` : "PJ-00000000";
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart, isInCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const mainImageRef = useRef(null);
  const videoRefs = useRef(new Map());

  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState("");
  const [similarProducts, setSimilarProducts] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState("");
  const [sizeError, setSizeError] = useState("");
  const [purchaseError, setPurchaseError] = useState("");
  const [addedToCart, setAddedToCart] = useState(false);
  const [addedSimilarProductId, setAddedSimilarProductId] = useState("");
  const [inWishlist, setInWishlist] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [productNotFound, setProductNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState("description");

  const [isZoomEnabled, setIsZoomEnabled] = useState(false);
  const [isInlineZoomVisible, setIsInlineZoomVisible] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });

  const INLINE_ZOOM_SCALE = 2.3;
  const canUseInlineZoom =
    typeof window === "undefined" || !window.matchMedia
      ? true
      : window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  const isVideo = (url) => {
    if (!url) {
      return false;
    }

    const videoExtensions = [".mp4", ".avi", ".mov", ".wmv", ".flv", ".webm"];
    return videoExtensions.some((extension) =>
      url.toLowerCase().includes(extension)
    );
  };

  const safePlayVideo = async (videoElement) => {
    if (!videoElement) {
      return;
    }

    try {
      await videoElement.play();
    } catch (error) {
      console.log("Video play prevented:", error.message);
    }
  };

  const safePauseVideo = (videoElement) => {
    if (!videoElement) {
      return;
    }

    try {
      videoElement.pause();
    } catch (error) {
      console.log("Video pause prevented:", error.message);
    }
  };

  const resetInlineZoom = useCallback(() => {
    setIsInlineZoomVisible(false);
    setZoomPosition({ x: 50, y: 50 });
  }, []);

  const updateInlineZoomPosition = useCallback((clientX, clientY) => {
    if (!mainImageRef.current) {
      return;
    }

    const rect = mainImageRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(clientY - rect.top, rect.height));

    setZoomPosition({
      x: (x / rect.width) * 100,
      y: (y / rect.height) * 100,
    });
  }, []);

  const handleImageMouseEnter = useCallback(
    (event) => {
      if (!canUseInlineZoom || !isZoomEnabled || isVideo(selectedImage)) {
        return;
      }

      setIsInlineZoomVisible(true);
      updateInlineZoomPosition(event.clientX, event.clientY);
    },
    [canUseInlineZoom, isZoomEnabled, selectedImage, updateInlineZoomPosition]
  );

  const handleImageMouseMove = useCallback(
    (event) => {
      if (!canUseInlineZoom || !isZoomEnabled || isVideo(selectedImage)) {
        return;
      }

      setIsInlineZoomVisible(true);
      updateInlineZoomPosition(event.clientX, event.clientY);
    },
    [canUseInlineZoom, isZoomEnabled, selectedImage, updateInlineZoomPosition]
  );

  const handleImageMouseLeave = useCallback(() => {
    resetInlineZoom();
  }, [resetInlineZoom]);

  const handleMainImageClick = useCallback(
    (event) => {
      if (!canUseInlineZoom || isVideo(selectedImage)) {
        return;
      }

      if (isZoomEnabled) {
        setIsZoomEnabled(false);
        resetInlineZoom();
        return;
      }

      setIsZoomEnabled(true);
      setIsInlineZoomVisible(true);
      updateInlineZoomPosition(event.clientX, event.clientY);
    },
    [
      canUseInlineZoom,
      isZoomEnabled,
      selectedImage,
      resetInlineZoom,
      updateInlineZoomPosition,
    ]
  );

  const fetchProductAndReviews = useCallback(async () => {
    try {
      const productResponse = await axios.get(`${API_BASE}/products/${id}`);
      const fetchedProduct = productResponse.data;

      setProductNotFound(false);
      setProduct(fetchedProduct);
      setSelectedImage(fetchedProduct?.images?.[0] || "");
      setQuantity(1);
      setSelectedSize("");
      setSizeError("");
      setPurchaseError("");
      setActiveTab("description");
      setAddedToCart(false);
      setAddedSimilarProductId("");
      setIsImageModalOpen(false);

      if (fetchedProduct?._id) {
        setInWishlist(isInWishlist(fetchedProduct._id));
      }

      const reviewsResponse = await axios.get(`${API_BASE}/reviews/product/${id}`);
      setReviews(reviewsResponse.data.reviews || []);
    } catch (error) {
      console.error("Error fetching product or reviews:", error);

      if (error?.response?.status === 404) {
        setProductNotFound(true);
        setProduct(null);
        setSelectedImage("");
      }

      setReviews([]);
    }
  }, [id, isInWishlist]);

  useEffect(() => {
    if (location.state && location.state.refreshReviews) {
      fetchProductAndReviews();
      window.history.replaceState({}, document.title);
      return;
    }

    fetchProductAndReviews();
  }, [fetchProductAndReviews, location.state]);

  useEffect(() => {
    const fetchSimilarProducts = async () => {
      try {
        const response = await axios.get(`${API_BASE}/products`, {
          params: {
            material: product?.material,
            product: product?.type,
            limit: 12,
          },
        });

        const filteredProducts = response.data.filter(
          (item) => item._id !== product._id
        );
        setSimilarProducts(filteredProducts);
      } catch (error) {
        console.error("Error fetching similar products:", error);
      }
    };

    if (product?._id) {
      fetchSimilarProducts();
    }
  }, [product]);

  useEffect(() => {
    const handleRefresh = () => fetchProductAndReviews();
    window.addEventListener("refreshProductReviews", handleRefresh);

    return () =>
      window.removeEventListener("refreshProductReviews", handleRefresh);
  }, [fetchProductAndReviews]);

  useEffect(
    () => bindStorefrontRefresh(fetchProductAndReviews, { scopes: ["products"] }),
    [fetchProductAndReviews]
  );

  useEffect(() => {
    const currentVideoRefs = videoRefs.current;

    return () => {
      currentVideoRefs.forEach((videoElement) => {
        if (videoElement) {
          try {
            videoElement.pause();
          } catch (error) {
            console.log("Video pause prevented:", error.message);
          }
        }
      });
      currentVideoRefs.clear();
    };
  }, []);

  useEffect(() => {
    setIsZoomEnabled(false);
    resetInlineZoom();
  }, [selectedImage, resetInlineZoom]);

  const mediaItems = Array.isArray(product?.images)
    ? product.images.filter(Boolean)
    : [];
  const descriptionPoints = getDescriptionPoints(product);
  const precautionPoints = getPrecautionPoints(product);
  const shippingCardItems = getShippingCardItems();
  const availableSizes = Array.isArray(product?.availableSizes)
    ? product.availableSizes.filter((size) => String(size || "").trim())
    : [];
  const stockQuantity = getStockQuantity(product?.stockQuantity);
  const isOutOfStock = stockQuantity === 0;
  const isLowStock = stockQuantity > 0 && stockQuantity <= LOW_STOCK_THRESHOLD;
  const currentPrice = Number.isFinite(Number(product?.offerPrice))
    ? Number(product.offerPrice)
    : Number(product?.price) || 0;
  const reviewCount = reviews.length > 0 ? reviews.length : Number(product?.reviews) || 0;
  const ratingValue =
    Number(product?.rating) > 0
      ? Number(product.rating)
      : reviewCount > 0
      ? Math.round(
          (reviews.reduce((total, review) => total + (Number(review.rating) || 0), 0) /
            reviewCount) *
            10
        ) / 10
      : 0;
  const reviewCountLabel = `${reviewCount} ${reviewCount === 1 ? "review" : "reviews"}`;
  const displaySku = getDisplaySku(product);
  const whyYoullLoveThis = [
    "Premium quality finish for a refined look",
    "Elegant design that feels occasion-ready",
    "Comfortable wear for long hours of styling",
  ];

  useEffect(() => {
    if (stockQuantity > 0 && quantity > stockQuantity) {
      setQuantity(stockQuantity);
    }
  }, [quantity, stockQuantity]);

  const handleAddToCart = () => {
    if (isOutOfStock) {
      setPurchaseError("Out of stock. Please wait for fresh inventory.");
      return;
    }

    if (availableSizes.length > 0 && !selectedSize) {
      setSizeError("Please select a size before adding this piece to your cart.");
      return;
    }

    if (quantity > stockQuantity) {
      setPurchaseError(`Only ${stockQuantity} item(s) left in stock.`);
      return;
    }

    const success = addToCart(product, quantity, selectedSize);

    if (success) {
      setSizeError("");
      setPurchaseError("");
      setAddedToCart(true);
      window.setTimeout(() => setAddedToCart(false), 2000);
    }
  };

  const handleBuyNow = () => {
    if (isOutOfStock) {
      setPurchaseError("Out of stock. Please wait for fresh inventory.");
      return;
    }

    if (availableSizes.length > 0 && !selectedSize) {
      setSizeError("Please select a size before continuing.");
      return;
    }

    if (quantity > stockQuantity) {
      setPurchaseError(`Only ${stockQuantity} item(s) left in stock.`);
      return;
    }

    const success = addToCart(product, quantity, selectedSize);

    if (success) {
      setSizeError("");
      setPurchaseError("");
      navigate("/cart");
    }
  };

  const handleWishlistToggle = () => {
    if (!product?._id) {
      return;
    }

    if (inWishlist) {
      removeFromWishlist(product._id);
      setInWishlist(false);
      return;
    }

    const success = addToWishlist(product);

    if (success) {
      setInWishlist(true);
    }
  };

  const handleShareProduct = async () => {
    const productUrl = typeof window !== "undefined" ? window.location.href : "";
    const shareData = {
      title: product?.title || "Product",
      text: `Take a look at ${product?.title || "this jewellery piece"}.`,
      url: productUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }

      if (navigator.clipboard && productUrl) {
        await navigator.clipboard.writeText(productUrl);
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.log("Product share failed:", error.message);
      }
    }
  };

  const handleSimilarProductAction = (similarProduct) => {
    const similarStockQuantity = getStockQuantity(similarProduct?.stockQuantity);
    const requiresSizeSelection =
      Array.isArray(similarProduct?.availableSizes) &&
      similarProduct.availableSizes.filter((size) => String(size || "").trim()).length > 0;

    if (similarStockQuantity === 0) {
      return;
    }

    if (requiresSizeSelection) {
      navigate(`/product/${similarProduct._id}`);
      return;
    }

    if (isInCart(similarProduct._id)) {
      navigate("/cart");
      return;
    }

    const success = addToCart(similarProduct, 1);

    if (success) {
      setAddedSimilarProductId(similarProduct._id);
      window.setTimeout(() => {
        setAddedSimilarProductId((currentId) =>
          currentId === similarProduct._id ? "" : currentId
        );
      }, 1800);
    }
  };

  const incrementQuantity = () => {
    setPurchaseError("");
    setQuantity((currentValue) =>
      currentValue < stockQuantity ? currentValue + 1 : currentValue
    );
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setPurchaseError("");
      setQuantity((currentValue) => currentValue - 1);
    }
  };

  const openImageModal = () => {
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
  };

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeImageModal();
      }
    };

    if (isImageModalOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isImageModalOpen]);

  if (productNotFound) {
    return <div className="product-detail-state">Product not found.</div>;
  }

  if (!product) {
    return <div className="product-detail-state">Loading product...</div>;
  }

  const selectedMedia = selectedImage || mediaItems[0] || "";
  const isSelectedMediaVideo = isVideo(selectedMedia);
  const mainImageZoomStyle =
    canUseInlineZoom && !isSelectedMediaVideo && isZoomEnabled && isInlineZoomVisible
      ? {
          transform: `scale(${INLINE_ZOOM_SCALE})`,
          transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
        }
      : undefined;

  return (
    <div className="product-detail-wrapper">
      <div className="product-detail-shell">
        <div className="product-detail-container">
          <section className={`image-section ${mediaItems.length <= 1 ? "image-section--single" : ""}`}>
            {mediaItems.length > 1 ? (
              <div className="thumbnail-images" aria-label="Product media gallery">
                {mediaItems.map((media, index) => (
                  <div
                    key={`${media}-${index}`}
                    className={`thumbnail-wrapper ${selectedMedia === media ? "is-active" : ""}`}
                  >
                    {isVideo(media) ? (
                      <video
                        src={media}
                        className={`thumbnail-video ${selectedMedia === media ? "active" : ""}`}
                        onClick={() => setSelectedImage(media)}
                        muted
                        onError={(event) => {
                          console.log("Thumbnail video error:", event.target.error);
                        }}
                        ref={(element) => {
                          if (element) {
                            videoRefs.current.set(`thumb-${index}`, element);
                          } else {
                            videoRefs.current.delete(`thumb-${index}`);
                          }
                        }}
                      />
                    ) : (
                      <img
                        src={media}
                        alt={`${product.title} preview ${index + 1}`}
                        className={selectedMedia === media ? "active" : ""}
                        onClick={() => setSelectedImage(media)}
                        loading="lazy"
                      />
                    )}
                    {isVideo(media) ? (
                      <div className="video-indicator">Video</div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}

            <div
              className={`main-media-frame ${
                mediaItems.length <= 1 ? "main-media-frame--full-width" : ""
              } ${selectedMedia && !isSelectedMediaVideo && canUseInlineZoom ? "zoomable-frame" : ""} ${
                isZoomEnabled ? "zoom-enabled" : ""
              }`}
              ref={mainImageRef}
              onClick={selectedMedia && !isSelectedMediaVideo ? handleMainImageClick : undefined}
              onDoubleClick={selectedMedia && !isSelectedMediaVideo ? openImageModal : undefined}
              onMouseEnter={selectedMedia && !isSelectedMediaVideo ? handleImageMouseEnter : undefined}
              onMouseLeave={selectedMedia && !isSelectedMediaVideo ? handleImageMouseLeave : undefined}
              onMouseMove={selectedMedia && !isSelectedMediaVideo ? handleImageMouseMove : undefined}
            >
              {isSelectedMediaVideo ? (
                <video
                  src={selectedMedia}
                  controls
                  className="main-video"
                  onClick={openImageModal}
                  onError={(event) => {
                    console.log("Main video error:", event.target.error);
                  }}
                  ref={(element) => {
                    if (element) {
                      videoRefs.current.set("main", element);
                    } else {
                      videoRefs.current.delete("main");
                    }
                  }}
                />
              ) : selectedMedia ? (
                <img
                  src={selectedMedia}
                  alt={product.title}
                  className="main-product-image"
                  draggable={false}
                  style={mainImageZoomStyle}
                />
              ) : (
                <div className="main-image-placeholder">Media unavailable</div>
              )}
            </div>
          </section>

          <aside className="detail-section">
            <div className="detail-actions" aria-label="Product actions">
              <button
                type="button"
                className="detail-action-button"
                onClick={handleShareProduct}
                aria-label="Share product"
              >
                <FaShareAlt />
              </button>
              <button
                type="button"
                className={`detail-action-button ${inWishlist ? "is-active" : ""}`}
                onClick={handleWishlistToggle}
                aria-label={inWishlist ? "Remove from wishlist" : "Add to wishlist"}
              >
                {inWishlist ? <FaHeart /> : <FaRegHeart />}
              </button>
            </div>

            <div className="detail-header">
              <h1>{product.title}</h1>
            </div>

            <div className="price-rating-row">
              <div className="price-block">
                <div className="price-box">
                  <span className="offer-price">{formatCurrency(currentPrice)}</span>
                  <span className="product-sku">SKU {displaySku}</span>
                </div>
              </div>

              <div
                className="rating-summary"
                aria-label={`Rating ${ratingValue.toFixed(1)} out of 5`}
              >
                <div className="rating-stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <FaStar
                      key={star}
                      color={star <= Math.round(ratingValue) ? "#b08d57" : "#ddd4c3"}
                    />
                  ))}
                </div>
                <div className="rating-copy">
                  <strong>{ratingValue.toFixed(1)}</strong>
                  <span>{reviewCountLabel}</span>
                </div>
              </div>
            </div>

            {isOutOfStock ? (
              <p className="stock-status stock-status--out">
                Currently out of stock. Please check back soon.
              </p>
            ) : isLowStock ? (
              <p className="stock-status stock-status--low">
                <FaExclamationCircle />
                <span>Only {stockQuantity} item(s) left in stock</span>
              </p>
            ) : null}

            <div className="why-love-section">
              <span className="why-love-kicker">Why you'll love this</span>
              <div className="why-love-list">
                {whyYoullLoveThis.map((item) => (
                  <div key={item} className="why-love-item">
                    <span className="why-love-bullet" aria-hidden="true">
                      •
                    </span>
                    <p>{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="selection-card">
              {availableSizes.length > 0 ? (
                <div className="size-selector">
                  <div className="selector-heading">
                    <label>Size</label>
                    {selectedSize ? <span>Selected: {selectedSize}</span> : null}
                  </div>

                  <div className="size-options">
                    {availableSizes.map((sizeValue) => (
                      <button
                        key={sizeValue}
                        type="button"
                        className={`size-option ${selectedSize === sizeValue ? "is-selected" : ""}`}
                        onClick={() => {
                          setSelectedSize(sizeValue);
                          setSizeError("");
                          setPurchaseError("");
                        }}
                      >
                        {sizeValue}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {!isOutOfStock ? (
                <div className="quantity-selector">
                  <div className="selector-heading">
                    <label>Quantity</label>
                    <span>{stockQuantity} available</span>
                  </div>

                  <div className="quantity-controls">
                    <button
                      type="button"
                      onClick={decrementQuantity}
                      disabled={quantity <= 1}
                      aria-label="Decrease quantity"
                    >
                      -
                    </button>
                    <span>{quantity}</span>
                    <button
                      type="button"
                      onClick={incrementQuantity}
                      disabled={quantity >= stockQuantity}
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                </div>
              ) : null}

              {sizeError ? <p className="size-error-message">{sizeError}</p> : null}
              {purchaseError ? <p className="size-error-message">{purchaseError}</p> : null}
            </div>

            <div className="button-group desktop-cta-group">
              {isOutOfStock ? (
                <button className="out-of-stock-btn" type="button" disabled>
                  Out of Stock
                </button>
              ) : (
                <>
                  <button className="add-to-cart" type="button" onClick={handleAddToCart}>
                    {addedToCart ? "Added to Cart" : "Add to Cart"}
                  </button>
                  <button className="buy-now" type="button" onClick={handleBuyNow}>
                    Buy Now
                  </button>
                </>
              )}
            </div>
          </aside>
        </div>

        <section className="product-tabs-section">
          <div className="section-heading">
            <span className="section-kicker">Product Details</span>
            <h2>Everything you need before you commit</h2>
          </div>

          <div className="product-tabs">
            <div className="product-tab-list" role="tablist" aria-label="Product details tabs">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "description"}
                className={activeTab === "description" ? "active" : ""}
                onClick={() => setActiveTab("description")}
              >
                Description
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "precautions"}
                className={activeTab === "precautions" ? "active" : ""}
                onClick={() => setActiveTab("precautions")}
              >
                Precautions
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "reviews"}
                className={activeTab === "reviews" ? "active" : ""}
                onClick={() => setActiveTab("reviews")}
              >
                Reviews
                <span>{reviewCount}</span>
              </button>
            </div>

            {activeTab === "description" ? (
              <div className="product-tab-panel product-tab-panel--description">
                <div className="tab-column">
                  <h3>Product description</h3>
                  <ul className="detail-bullet-list">
                    {descriptionPoints.map((point, index) => (
                      <li key={`${point}-${index}`}>{point}</li>
                    ))}
                  </ul>
                </div>

                <aside className="shipping-detail-card">
                  <span className="shipping-detail-kicker">Shipping details</span>
                  <h3>Delivery information</h3>
                  <ul className="shipping-detail-list">
                    {shippingCardItems.map((item, index) => (
                      <li key={`${item}-${index}`}>
                        <span className="shipping-detail-dot" aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </aside>
              </div>
            ) : activeTab === "precautions" ? (
              <div className="product-tab-panel product-tab-panel--precautions">
                <aside className="detail-info-card detail-info-card--precautions">
                  <span className="detail-info-kicker">Care notes</span>
                  <h3>Precautions</h3>
                  <ul className="detail-card-list">
                    {precautionPoints.map((item, index) => (
                      <li key={`${item}-${index}`}>
                        <span className="detail-card-dot" aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </aside>
              </div>
            ) : (
              <div className="product-tab-panel product-tab-panel--reviews">
                <div className="reviews-overview">
                  <span className="reviews-overview-label">Customer sentiment</span>
                  <div className="reviews-overview-score">
                    <strong>{ratingValue.toFixed(1)}</strong>
                    <div>
                      <div className="rating-stars">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <FaStar
                            key={star}
                            color={star <= Math.round(ratingValue) ? "#b08d57" : "#ddd4c3"}
                          />
                        ))}
                      </div>
                      <span>{reviewCountLabel}</span>
                    </div>
                  </div>
                </div>

                <div className="reviews-list">
                  {reviews.length === 0 ? (
                    <div className="reviews-empty-state">
                      <h3>No reviews yet</h3>
                      <p>Verified customers can share feedback after delivery.</p>
                    </div>
                  ) : (
                    reviews.map((review) => (
                      <article key={review._id} className="review-card">
                        <div className="review-card-header">
                          <div>
                            <strong>{review.user?.name || "Customer"}</strong>
                            <span>Verified buyer</span>
                          </div>

                          <div className="review-card-rating">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <FaStar
                                key={star}
                                color={star <= review.rating ? "#b08d57" : "#ddd4c3"}
                              />
                            ))}
                          </div>
                        </div>

                        <p>{review.feedback}</p>
                        <time>{formatReviewDate(review.createdAt)}</time>
                      </article>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        {similarProducts.length > 0 ? (
          <section className="similar-products-section">
            <div className="section-heading">
              <span className="section-kicker">Curated Suggestions</span>
              <h2>You May Also Like</h2>
            </div>

            <Swiper
              modules={[Navigation]}
              className="similar-products-slider"
              spaceBetween={18}
              slidesPerView={4}
              navigation
              breakpoints={{
                0: { slidesPerView: 1.1 },
                640: { slidesPerView: 2.1 },
                980: { slidesPerView: 3.1 },
                1280: { slidesPerView: 4 },
              }}
            >
              {similarProducts.map((similarProduct) => {
                const primaryMedia = similarProduct.images?.[0];
                const similarStockQuantity = getStockQuantity(
                  similarProduct?.stockQuantity
                );
                const similarOutOfStock = similarStockQuantity === 0;
                const requiresSizeSelection =
                  Array.isArray(similarProduct?.availableSizes) &&
                  similarProduct.availableSizes.filter((size) =>
                    String(size || "").trim()
                  ).length > 0;
                const quickActionLabel = similarOutOfStock
                  ? "Out of Stock"
                  : requiresSizeSelection
                  ? "Select Size"
                  : isInCart(similarProduct._id)
                  ? "Go to Cart"
                  : addedSimilarProductId === similarProduct._id
                  ? "Added"
                  : "Quick Add";

                return (
                  <SwiperSlide key={similarProduct._id}>
                    <article
                      className="similar-product-card"
                      onClick={() => navigate(`/product/${similarProduct._id}`)}
                    >
                      <div className="similar-product-media-frame">
                        {isVideo(primaryMedia) ? (
                          <div className="similar-product-video-wrapper">
                            <video
                              src={primaryMedia}
                              className="similar-product-video"
                              muted
                              onMouseEnter={(event) => safePlayVideo(event.target)}
                              onMouseLeave={(event) => safePauseVideo(event.target)}
                              onError={(event) => {
                                console.log(
                                  "Similar product video error:",
                                  event.target.error
                                );
                              }}
                            />
                            <div className="similar-product-video-indicator">Video</div>
                          </div>
                        ) : primaryMedia ? (
                          <img
                            className="similar-product-image"
                            src={primaryMedia}
                            alt={similarProduct.title}
                            loading="lazy"
                          />
                        ) : (
                          <div className="similar-product-empty">No image</div>
                        )}
                      </div>

                      <div className="similar-product-copy">
                        <div className="similar-product-rating">
                          <FaStar />
                          <span>
                            {(Number(similarProduct.rating) || 0).toFixed(1)}
                          </span>
                          <small>({Number(similarProduct.reviews) || 0})</small>
                        </div>

                        <h3>{similarProduct.title}</h3>

                        <div className="similar-product-pricing">
                          <strong>
                            {formatCurrency(
                              Number.isFinite(Number(similarProduct.offerPrice))
                                ? similarProduct.offerPrice
                                : similarProduct.price
                            )}
                          </strong>
                        </div>

                        <button
                          type="button"
                          className="similar-product-cta"
                          disabled={similarOutOfStock}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleSimilarProductAction(similarProduct);
                          }}
                        >
                          {quickActionLabel}
                        </button>
                      </div>
                    </article>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </section>
        ) : null}
      </div>

      <div className="mobile-sticky-bar">
        {isOutOfStock ? (
          <button className="out-of-stock-btn" type="button" disabled>
            Out of Stock
          </button>
        ) : (
          <>
            <div className="mobile-sticky-price">
              <span>Price</span>
              <strong>{formatCurrency(currentPrice)}</strong>
            </div>
            <div className="mobile-sticky-actions">
              <button className="add-to-cart" type="button" onClick={handleAddToCart}>
                {addedToCart ? "Added" : "Add to Cart"}
              </button>
              <button className="buy-now" type="button" onClick={handleBuyNow}>
                Buy Now
              </button>
            </div>
          </>
        )}
      </div>

      {isImageModalOpen ? (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(event) => event.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeImageModal}>
              <FaTimes />
            </button>

            <div className="modal-image-container">
              {isVideo(selectedMedia) ? (
                <video
                  src={selectedMedia}
                  controls
                  className="modal-video"
                  onError={(event) => {
                    console.log("Modal video error:", event.target.error);
                  }}
                  ref={(element) => {
                    if (element) {
                      videoRefs.current.set("modal-main", element);
                    } else {
                      videoRefs.current.delete("modal-main");
                    }
                  }}
                />
              ) : (
                <img src={selectedMedia} alt={`${product.title} enlarged view`} />
              )}
            </div>

            <div className="modal-thumbnails">
              {mediaItems.map((media, index) => (
                <div
                  key={`${media}-${index}`}
                  className={`modal-thumbnail-wrapper ${selectedMedia === media ? "is-active" : ""}`}
                >
                  {isVideo(media) ? (
                    <video
                      src={media}
                      className={`modal-thumbnail-video ${
                        selectedMedia === media ? "active" : ""
                      }`}
                      onClick={() => setSelectedImage(media)}
                      muted
                      onError={(event) => {
                        console.log(
                          "Modal thumbnail video error:",
                          event.target.error
                        );
                      }}
                      ref={(element) => {
                        if (element) {
                          videoRefs.current.set(`modal-thumb-${index}`, element);
                        } else {
                          videoRefs.current.delete(`modal-thumb-${index}`);
                        }
                      }}
                    />
                  ) : (
                    <img
                      src={media}
                      alt={`${product.title} thumbnail ${index + 1}`}
                      className={selectedMedia === media ? "active" : ""}
                      onClick={() => setSelectedImage(media)}
                    />
                  )}
                  {isVideo(media) ? (
                    <div className="modal-video-indicator">Video</div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ProductDetail;
